"use client";

import * as React from "react";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { cn } from "../../../lib/cn";
import { Button } from "../../../primitives/Button";
import { Textarea } from "../../../primitives/Textarea";
import { Label } from "../../../primitives/Label";
import { PanelHeader, PanelFooter } from "./PanelHeader";
import { validateUrls, parseUrlLines } from "../../../lib/fileUtils";

/**
 * UrlPanel — import one or more web / GitHub URLs. LIVE panel.
 *
 * The richest panel, ported from the donor's `UrlInputView.tsx` with the
 * validation logic kept VERBATIM (via `validateUrls` / `parseUrlLines` in
 * `lib/fileUtils`):
 *   - multi-URL textarea (one link per line),
 *   - `validateUrls` against `/^https?:\/\/.+\..+/i` -> inline
 *     `Invalid URL: ...` banner with an `AlertCircle`,
 *   - a live "{n} link(s) detected" counter,
 *   - Enter-inserts-a-newline (Shift+Enter excluded), restoring the caret
 *     after the inserted `\n` via a microtask,
 *   - the "We'll extract / Notes & Guidelines" info block.
 *
 * Submit is disabled while the input is empty or a validation error stands.
 * The donor's navy hex / `blue-50` / `red-50` / `amber-*` become brand tokens
 * (`text-primary`, `bg-muted/40`, `destructive`, `warning`).
 *
 * Controlled: the parent owns `value`. The validation error is local panel
 * state (cleared on edit), exactly as the donor.
 */
export interface UrlPanelProps {
  /** Multi-line URL textarea value (controlled). */
  value: string;
  /** Change handler. */
  onChange: (value: string) => void;
  /** Back-to-grid handler. */
  onBack: () => void;
  /** Submit handler — fired only when every line validates. */
  onSubmit: () => void;
  /** Submit button label. Default "Add to vault". */
  submitLabel?: string;
  className?: string;
}

export const UrlPanel: React.FC<UrlPanelProps> = ({
  value,
  onChange,
  onBack,
  onSubmit,
  submitLabel = "Add to vault",
  className,
}) => {
  const reactId = React.useId();
  const fieldId = `${reactId}-urls`;
  const errorId = `${reactId}-error`;
  const [error, setError] = React.useState("");

  const handleChange = (next: string) => {
    onChange(next);
    if (error) setError("");
  };

  const handleSubmit = () => {
    const result = validateUrls(value);
    if (!result.valid) {
      setError(result.error);
      return;
    }
    setError("");
    onSubmit();
  };

  // Enter inserts a newline at the caret (Shift+Enter excluded); the caret is
  // restored one position past the inserted "\n" on the next tick. Ported
  // verbatim from the donor.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const target = e.currentTarget;
      const cursor = target.selectionStart ?? value.length;
      const next = `${value.substring(0, cursor)}\n${value.substring(cursor)}`;
      onChange(next);
      requestAnimationFrame(() => {
        target.selectionStart = target.selectionEnd = cursor + 1;
      });
    }
  };

  const urlCount = parseUrlLines(value).length;
  const disabled = !value.trim() || Boolean(error);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <PanelHeader
        title="Add web content"
        description="Import content from webpages or GitHub repositories"
        onBack={onBack}
      />

      <div className="flex flex-col gap-4">
        <div>
          <Label htmlFor={fieldId} className="mb-1.5 block">
            Enter one or more web or GitHub URLs
          </Label>
          <Textarea
            id={fieldId}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={"Paste links like https://example.com or https://github.com/user/repo\nOne per line."}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? errorId : undefined}
            className="min-h-[160px] resize-none leading-relaxed"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Each link should be on a new line. We&apos;ll import page content and metadata.
            </p>
            {urlCount > 0 ? (
              <span className="shrink-0 text-sm font-medium text-primary">
                {urlCount} link{urlCount > 1 ? "s" : ""} detected
              </span>
            ) : null}
          </div>
        </div>

        {error ? (
          <div
            id={errorId}
            role="alert"
            className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-destructive"
          >
            <AlertCircle className="size-4 shrink-0" aria-hidden />
            <span className="text-sm">{error}</span>
          </div>
        ) : null}

        <div className="space-y-3 rounded-md bg-muted/40 p-4 text-sm text-muted-foreground">
          <div>
            <p className="mb-1.5 font-medium text-foreground">We&apos;ll extract:</p>
            <ul className="list-inside list-disc space-y-1 text-xs">
              <li>Main page content, metadata, and title.</li>
              <li>GitHub: README and documentation files.</li>
            </ul>
          </div>
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <AlertTriangle className="size-3.5 text-warning" aria-hidden />
              <p className="font-medium text-warning">Notes &amp; guidelines:</p>
            </div>
            <ul className="list-inside list-disc space-y-1 text-xs">
              <li>Only publicly visible content is imported.</li>
              <li>Avoid uploading copyrighted, confidential, or sensitive materials.</li>
              <li>We follow website permissions and skip blocked or paywalled pages.</li>
            </ul>
          </div>
        </div>
      </div>

      <PanelFooter>
        <Button type="button" variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button type="button" disabled={disabled} onClick={handleSubmit}>
          {submitLabel}
        </Button>
      </PanelFooter>
    </div>
  );
};
UrlPanel.displayName = "UrlPanel";
